import React from "react";
import { styled } from "@mui/system";
import { Typography, Box } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import EmailIcon from "@mui/icons-material/Email";
import TikTokIcon from "@mui/icons-material/MusicNote";

// =====================================================
// CONTENEDOR PRINCIPAL DEL FOOTER
// =====================================================
const FooterContainer = styled("footer")({
  position: "relative",

  // Imagen de fondo
  backgroundImage:
    "url('https://mir-s3-cdn-cf.behance.net/project_modules/fs/e91584115490405.604faa9155f62.jpg')",

  // IMPORTANTE:
  // No utiliza "cover", por lo tanto no recorta los extremos.
  backgroundSize: "100% 100%",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",

  color: "white",
  width: "100%",
  textAlign: "center",

  padding: "55px 20px 22px",

  overflow: "hidden",

  // Degradado sobre la imagen
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,

    background:
      "linear-gradient(180deg, rgba(18, 65, 76, 0.38), rgba(10, 40, 50, 0.82))",

    zIndex: 0,
  },

  // Todo el contenido queda encima del degradado
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
});

// =====================================================
// REDES SOCIALES
// =====================================================
const SocialIcons = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  gap: "18px",

  marginTop: "24px",

  "& a": {
    width: "48px",
    height: "48px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    color: "white",
    textDecoration: "none",

    borderRadius: "50%",

    background: "rgba(255, 255, 255, 0.13)",

    border: "1px solid rgba(255, 255, 255, 0.35)",

    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",

    transition: "all 0.3s ease",
  },

  "& a:hover": {
    color: "#ffdd57",

    background: "rgba(255, 255, 255, 0.24)",

    borderColor: "#ffdd57",

    transform: "translateY(-4px) scale(1.05)",

    boxShadow: "0 7px 20px rgba(0, 0, 0, 0.30)",
  },
});

// =====================================================
// CONTENEDOR DEL CORREO
// =====================================================
const Contact = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  marginTop: "24px",
});

// =====================================================
// BOTÓN DEL CORREO
// =====================================================
const ContactEmail = styled("a")({
  display: "inline-flex",

  alignItems: "center",
  justifyContent: "center",

  gap: "10px",

  padding: "12px 22px",

  color: "white",

  textDecoration: "none",

  fontSize: "14px",

  fontWeight: 500,

  borderRadius: "30px",

  background: "rgba(255, 255, 255, 0.14)",

  border: "1px solid rgba(255, 255, 255, 0.38)",

  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",

  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.12)",

  transition: "all 0.3s ease",

  "&:hover": {
    color: "#ffdd57",

    background: "rgba(255, 255, 255, 0.24)",

    borderColor: "#ffdd57",

    transform: "translateY(-3px)",

    boxShadow: "0 8px 22px rgba(0, 0, 0, 0.25)",
  },

  // En celulares
  "@media (max-width: 600px)": {
    fontSize: "13px",
    padding: "10px 16px",
  },
});

// =====================================================
// SEPARADOR
// =====================================================
const Divider = styled("div")({
  margin: "30px auto 13px",

  width: "65px",

  height: "4px",

  backgroundColor: "#ffdd57",

  borderRadius: "10px",
});

// =====================================================
// FOOTER
// =====================================================
function Footer() {
  return (
    <FooterContainer>

      {/* ==========================================
          TÍTULO
      ========================================== */}
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{
          textShadow: "0 2px 9px rgba(0,0,0,0.60)",
        }}
      >
        Turismo San Román
      </Typography>

      {/* ==========================================
          SUBGERENCIA
      ========================================== */}
      <Typography
        variant="subtitle1"
        fontWeight="500"
        sx={{
          mt: 0.5,

          opacity: 0.96,

          textShadow: "0 2px 7px rgba(0,0,0,0.60)",
        }}
      >
        Subgerencia de Promoción y Competitividad
      </Typography>

      {/* ==========================================
          DESCRIPCIÓN
      ========================================== */}
      <Typography
        variant="body2"
        sx={{
          maxWidth: 560,

          margin: "13px auto 0",

          opacity: 0.95,

          lineHeight: 1.7,

          textShadow: "0 2px 6px rgba(0,0,0,0.55)",
        }}
      >
        Conectamos cultura, historia y experiencias únicas para que descubras
        lo mejor de nuestra región.
      </Typography>

      {/* ==========================================
          REDES SOCIALES
      ========================================== */}
      <SocialIcons>

        {/* FACEBOOK */}
        <a
          href="https://www.facebook.com/profile.php?id=61590191527672"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook"
        >
          <FacebookIcon fontSize="medium" />
        </a>

        {/* TIKTOK */}
        <a
          href="https://www.tiktok.com/@sgpyc_muni_juliaca?_r=1"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="TikTok"
        >
          <TikTokIcon fontSize="medium" />
        </a>

      </SocialIcons>

      {/* ==========================================
          CORREO
      ========================================== */}
      <Contact>

        <ContactEmail
          href="https://mail.google.com/mail/?view=cm&fs=1&to=promocionycompetitividad@gmail.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Enviar correo a Promoción y Competitividad"
        >
          <EmailIcon fontSize="small" />

          <span>
            promocionycompetitividad@gmail.com
          </span>
        </ContactEmail>

      </Contact>

      {/* ==========================================
          SEPARADOR
      ========================================== */}
      <Divider />

      {/* ==========================================
          COPYRIGHT
      ========================================== */}
      <Typography
        variant="caption"
        sx={{
          opacity: 0.9,

          textShadow: "0 2px 5px rgba(0,0,0,0.55)",
        }}
      >
        © {new Date().getFullYear()} Turismo San Román · Todos los derechos
        reservados
      </Typography>

    </FooterContainer>
  );
}

export default Footer;